import { DAY_SLOTS, fmtDayShort, zoneLabel } from '@concord/domain';
import { Button, Notice, QCard, WizardNav } from '@concord/ui';
import { useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { useSubmit } from '../api';
import type { StepProps } from '../IntakeWizard';
import { summaryValue } from '../steps';
import s from './steps.module.css';

/** INT-10: every answer in one place, the terms actively accepted, then send. */
export function ReviewStep({ v, back, go, mode, onSent }: StepProps) {
  const submit = useSubmit(v.id);
  const [accepted, setAccepted] = useState(false);
  const tz = v.patient.timezone;
  const label = (time: string) => DAY_SLOTS.find((d) => d.time === time)?.label ?? time;
  const byDay = new Map<string, string[]>();
  for (const t of v.times) {
    const day = fmtDayShort(new Date(t.startsAt), tz);
    byDay.set(day, [...(byDay.get(day) ?? []), label(t.time)]);
  }
  const rows: { key: string; k: string; v: React.ReactNode }[] = [
    { key: 'type', k: 'Case type', v: v.caseType?.term ?? '—' },
    { key: 'panel', k: 'Expert panel', v: v.pool?.publicName ?? '—' },
    {
      key: 'about',
      k: mode === 'staff' ? 'About the patient' : 'About you',
      v: summaryValue(v, 'about').value,
    },
    {
      key: 'history',
      k: 'History',
      v: (
        <div className={s.clip}>
          <p>{v.history}</p>
        </div>
      ),
    },
    {
      key: 'history',
      k: 'Questions',
      v: (
        <ol>
          {v.questions.map((q) => (
            <li key={q}>{q}</li>
          ))}
        </ol>
      ),
    },
    {
      key: 'records',
      k: 'Records',
      v: v.attachments.length
        ? v.attachments.map((a) => a.description ?? a.filename).join(' · ')
        : 'None added',
    },
    {
      key: 'times',
      k: 'Times offered',
      v: v.times.length
        ? `${[...byDay].map(([d, ts]) => `${d}: ${ts.join(', ')}`).join(' · ')} (${zoneLabel(tz)})`
        : 'None yet. At least one is needed before the case can go to physicians.',
    },
  ];

  return (
    <>
      <QCard
        label="Review and send"
        title="Check everything before it goes"
        lead="Nothing is sent to a physician until the care navigator releases the case. You can still change any answer here."
      >
        <div className={s.review}>
          {rows.map((r) => (
            <div key={r.k} className={s.rv}>
              <span className={s.k}>{r.k}</span>
              <div className={s.v}>{r.v}</div>
              <button type="button" className={s.ed} onClick={() => go(r.key)}>
                Change
              </button>
            </div>
          ))}
        </div>
      </QCard>
      <QCard label="Terms of use" title="Terms of use" lead={`Version ${v.terms.version}`}>
        <div className={s.terms}>{v.terms.body}</div>
        <label className="check">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          <span>
            {mode === 'staff'
              ? `The patient has heard and accepts the terms of use, version ${v.terms.version}.`
              : `I have read and accept the terms of use, version ${v.terms.version}.`}
          </span>
        </label>
        {submit.isError && <Notice tone="bad">{errorMessage(submit.error)}</Notice>}
      </QCard>
      <WizardNav
        back={
          <Button variant="sec" onClick={back}>
            Back to times
          </Button>
        }
        status={accepted ? 'Ready to send' : 'Accept the terms to send'}
        next={
          <Button
            size="lg"
            disabled={!accepted}
            busy={submit.isPending}
            onClick={async () => {
              const r = await submit.mutateAsync(v.terms.version).catch(() => null);
              if (r) onSent(r.reference);
            }}
          >
            {mode === 'staff' ? 'Send the case' : 'Send my case'}
          </Button>
        }
      />
    </>
  );
}
